import { type PicoXConfiguration } from '../models/configuration';
import { type StoredEvent } from '../models/event';
import { PicoXPayloadTooLargeError, PicoXSerializationError, PicoXServerError } from './exceptions';
import { PicoXClientError } from './exceptions';
import { type TimestampCorrection } from './scheduler';

/**
 * Network implements API calls to the Pico server.
 *
 * It exports a method 'sendEvents' which sends events to the server with the browser `fetch` API.
 *
 * It defines two kinds of error:
 * - Server error (5xx status codes): will be retried
 * - Client error (4xx status codes): will be logged and not retried
 *
 * Send events returns a TimestampCorrection object, see [TimestampCorrection](/picox-web/src/internal/scheduler.ts)
 * for more information.
 */
export type Network = {
  /** Sends events to the server */
  sendEvents: (
    events: StoredEvent[],
    timestampCorrection: TimestampCorrection,
  ) => Promise<TimestampCorrection>;
};

/** Network-related errors */
export const NetworkError = {
  /** Server returned an error */
  ServerError: 'ServerError',
  /** An error due to the client */
  ClientError: 'ClientError',
  /** An error due to the serialization of the events */
  SerializationError: 'SerializationError',
} as const;

export const createNetwork = (
  config: PicoXConfiguration,
  timeProvider: () => Date = () => new Date(),
): Network => {
  return {
    sendEvents: async (
      events: StoredEvent[],
      timestampCorrection: TimestampCorrection,
    ): Promise<TimestampCorrection> => {
      if (events.length === 0) {
        return timestampCorrection;
      }

      let body: string;
      try {
        body = JSON.stringify({
          events: events,
          delta: timestampCorrection.delta,
          last_event_timestamp: timestampCorrection.last_event_timestamp,
          request_timestamp: timeProvider().toISOString(),
        });
      } catch (error) {
        config.logger.error(`Failed to serialize events: ${error}`);
        throw PicoXSerializationError('SerializationError');
      }

      const headers: Record<string, string> = {
        'X-Pico-Auth': config.identificationToken,
        'Content-Type': 'application/json',
      };

      // We only expose this header when developing so that we
      // reduce the risk of abuse of it.
      if (config.isDevelopment === true) {
        headers['X-Pico-Is-Development'] = 'true';
      }

      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      if (response.status !== 200) {
        config.logger.error(`Received invalid response: ${response.status}`);
        if (response.status === 413) {
          throw PicoXPayloadTooLargeError('ClientError');
        }
        if (400 <= response.status && response.status < 500) {
          throw PicoXClientError('ClientError');
        }
        throw PicoXServerError('ServerError');
      }

      const json = await response.json();
      return {
        delta: json.delta ?? 0,
        last_event_timestamp: json.last_event_timestamp ?? null,
      };
    },
  };
};
